import { noop } from 'lodash';
import { useCallback } from 'react';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';

import { MobileFiltersSubMenu } from '~/components/FiltersMenu/MobileFiltersSubMenu';
import { type DateFilters, type GetFiltersMenuOptionsParams, sortFiltersByOrder } from '~/components/FiltersMenu/types';
import { getAdvancedFiltersOptions } from '~/components/FiltersMenu/utils/getAdvancedFiltersOptions';
import { getAudioVisualFiltersOptions } from '~/components/FiltersMenu/utils/getAudioVisualFiltersOptions';
import { getCustomFieldsFilterOptions } from '~/components/FiltersMenu/utils/getCustomFieldFiltersOptions';
import { getDateFiltersOptions } from '~/components/FiltersMenu/utils/getDateFiltersOptions';
import { getFilterProps } from '~/components/FiltersMenu/utils/getFilterProps';
import { getLocationFiltersOptions } from '~/components/FiltersMenu/utils/getLocationFiltersOptions';
import {
  isAdvancedFilter,
  isAudioVisualFilter,
  isDateFilter,
  isLocationFilter,
} from '~/components/FiltersMenu/utils/typeChecks';

export const useFiltersMenuOptions = () => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const [showMobileFiltersSubMenu] = useAirModal(MobileFiltersSubMenu);

  const getFiltersMenuOptions = useCallback(
    ({
      selectedFilters = [],
      onSelect,
      customFields,
      availableFilters,
      disabledFilters,
    }: GetFiltersMenuOptionsParams): DropdownMenuOption[] => {
      let filtersToShow = availableFilters;
      if (!customFields.length) {
        filtersToShow = availableFilters.filter((filter) => filter !== 'customField');
      }

      filtersToShow = filtersToShow.filter((filter) => filter !== 'assetFileKind');

      // date filters should be visible as suboption - filter them out from this array
      const dateFilters: DateFilters[] = availableFilters.filter(isDateFilter).sort(sortFiltersByOrder);
      filtersToShow = filtersToShow.filter((filter) => !isDateFilter(filter));

      const locationFilters = availableFilters.filter(isLocationFilter).sort(sortFiltersByOrder);
      filtersToShow = filtersToShow.filter((filter) => !isLocationFilter(filter));

      const audioVisualFilters = availableFilters.filter(isAudioVisualFilter).sort(sortFiltersByOrder);
      filtersToShow = filtersToShow.filter((filter) => !isAudioVisualFilter(filter));

      const advancedFilters = availableFilters.filter(isAdvancedFilter).sort(sortFiltersByOrder);
      filtersToShow = filtersToShow.filter((filter) => !isAdvancedFilter(filter));

      // if there is no 'date' filter, but we have any date filters included, add 'date' filter (parent of date suboptions)
      if (!filtersToShow.includes('date') && dateFilters.length > 0) {
        filtersToShow.splice(filtersToShow.length - 2, 0, 'date');
      } else if (dateFilters.length === 0 && filtersToShow.includes('date')) {
        filtersToShow = filtersToShow.filter((filter) => filter !== 'date');
      }

      if (!filtersToShow.includes('audioVisual') && audioVisualFilters.length > 0) {
        filtersToShow.splice(filtersToShow.length - 2, 0, 'audioVisual');
      } else if (audioVisualFilters.length === 0 && filtersToShow.includes('audioVisual')) {
        filtersToShow = filtersToShow.filter((filter) => filter !== 'audioVisual');
      }

      // if there is no 'location' filter, but we have any location filters included, add 'location' filter (parent of date suboptions)
      if (!filtersToShow.includes('location') && locationFilters.length > 0) {
        filtersToShow.splice(filtersToShow.length - 2, 0, 'location');
      } else if (dateFilters.length === 0 && filtersToShow.includes('location')) {
        filtersToShow = filtersToShow.filter((filter) => filter !== 'location');
      }

      if (!filtersToShow.includes('advanced') && advancedFilters.length > 0) {
        filtersToShow.splice(filtersToShow.length - 2, 0, 'advanced');
      } else if (advancedFilters.length === 0 && filtersToShow.includes('advanced')) {
        filtersToShow = filtersToShow.filter((filter) => filter !== 'advanced');
      }

      return filtersToShow.map((filterType) => {
        if (filterType === 'customField' && !!customFields.length) {
          return {
            ...getFilterProps({
              type: 'customField',
              onSelect: !isAboveMediumScreen
                ? () =>
                    showMobileFiltersSubMenu({
                      options: getCustomFieldsFilterOptions({ customFields, selectedFilters, onSelect }),
                    })
                : noop,
            }),
            options: getCustomFieldsFilterOptions({ customFields, selectedFilters, onSelect }),
            'data-id': 'customField',
            type: !isAboveMediumScreen ? 'item' : 'sub',
          };
        } else if (filterType === 'location') {
          return {
            ...getFilterProps({
              type: 'location',
              onSelect: !isAboveMediumScreen
                ? () =>
                    showMobileFiltersSubMenu({
                      options: getLocationFiltersOptions({ selectedFilters, locationFilters, onSelect }),
                    })
                : noop,
            }),
            'data-id': 'location',
            options: getLocationFiltersOptions({ selectedFilters, locationFilters, onSelect, disabledFilters }),
            type: !isAboveMediumScreen ? 'item' : 'sub',
          };
        } else if (filterType === 'advanced') {
          return {
            ...getFilterProps({
              type: 'advanced',
              onSelect: !isAboveMediumScreen
                ? () =>
                    showMobileFiltersSubMenu({
                      options: getAdvancedFiltersOptions({
                        selectedFilters,
                        advancedFilters,
                        onSelect,
                        disabledFilters,
                      }),
                    })
                : noop,
            }),
            'data-id': 'advanced',
            options: getAdvancedFiltersOptions({ selectedFilters, advancedFilters, onSelect, disabledFilters }),
            type: !isAboveMediumScreen ? 'item' : 'sub',
          };
        } else if (filterType === 'audioVisual') {
          return {
            ...getFilterProps({
              type: 'audioVisual',
              onSelect: !isAboveMediumScreen
                ? () =>
                    showMobileFiltersSubMenu({
                      options: getAudioVisualFiltersOptions({
                        selectedFilters,
                        audioVisualFilters,
                        onSelect,
                        disabledFilters,
                      }),
                    })
                : noop,
            }),
            'data-id': 'audioVisual',
            options: getAudioVisualFiltersOptions({ selectedFilters, audioVisualFilters, onSelect, disabledFilters }),
            type: !isAboveMediumScreen ? 'item' : 'sub',
          };
        } else if (filterType === 'date') {
          return {
            ...getFilterProps({
              type: 'date',
              onSelect: !isAboveMediumScreen
                ? () =>
                    showMobileFiltersSubMenu({
                      options: getDateFiltersOptions({ selectedFilters, dateFilters, onSelect, disabledFilters }),
                    })
                : noop,
            }),
            'data-id': 'date',
            options: getDateFiltersOptions({ selectedFilters, dateFilters, onSelect, disabledFilters }),
            type: !isAboveMediumScreen ? 'item' : 'sub',
          };
        } else if (filterType.includes('separator')) {
          return {
            id: filterType,
            type: 'separator',
          };
        } else {
          return {
            ...getFilterProps({
              type: filterType,
              onSelect,
              disabled: disabledFilters?.includes(filterType),
              isSelected: selectedFilters.some(({ type }) => type === filterType),
            }),
            type: 'item',
          };
        }
      });
    },
    [isAboveMediumScreen, showMobileFiltersSubMenu],
  );

  return {
    getFiltersMenuOptions,
  };
};
